import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { type SubmitHandler, useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { MdMailOutline } from 'react-icons/md';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { LOCALE_REPLACEMENT_FOR_RECAPTCHA } from '@repo/gatsby-localization-helpers';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import { Label } from '@repo/ui-components/shadcn/Label';
import { Input } from '@repo/ui-components/shadcn/Input';
import { Button } from '@repo/ui-components/shadcn/Button';
import { Checkbox } from '@repo/ui-components/shadcn/Checkbox';
import { localeToKlaviyoPublicMap } from '@repo/utils/klaviyo';

interface Props {
  variantIdNumber: string;
  oosKey: string;
}

export default function OutOfStock({
  variantIdNumber,
  oosKey,
}: Readonly<Props>) {
  const { customer } = useContext(CustomerContext);
  const { tt, locale } = useUiComponentsInternationalContext();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [formErrors, setFormErrors] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oosToken, setOosToken] = useState(null);
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  const recaptchaLocale = LOCALE_REPLACEMENT_FOR_RECAPTCHA?.[locale] ?? locale;

  const isBrowser = typeof window !== 'undefined';

  const validationSchema = useMemo(
    () =>
      yup
        .object({
          userEmail: yup
            .string()
            .email(tt(s => s.outOfStockFormComponent.emailValidationMessage))
            .required(
              tt(s => s.outOfStockFormComponent.requiredValidationMessage)
            ),
        })
        .required(),
    [tt]
  );

  type FormData = yup.InferType<typeof validationSchema>;

  useEffect(() => {
    if (isBrowser) {
      const oosLocalStorage = window.localStorage.getItem(oosKey);
      if (oosLocalStorage === null) {
        setOosToken(false);
      } else {
        setOosToken(true);
      }
    }
  }, [isBrowser, oosKey, variantIdNumber]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
  });

  const handleEmailSignup: SubmitHandler<FormData> = async (
    { userEmail },
    event
  ) => {
    event.preventDefault();
    setSubmitDisabled(true);
    setIsLoading(true);
    setFormErrors(null); // Clear previous errors

    const gRecaptchaResponse = recaptchaRef.current
      ? await recaptchaRef.current.execute()
      : null;

    if (!gRecaptchaResponse) {
      setSubmitDisabled(false);
      setIsLoading(false);
      setFormErrors(
        tt(s => s.outOfStockFormComponent.recaptchaErrorMessage) ||
          'Please complete the CAPTCHA.'
      );
      return;
    }

    try {
      // 1. Subscribe to back in stock (no marketing_opt_in here)
      await axios.request({
        method: 'POST',
        url: `https://a.klaviyo.com/client/back-in-stock-subscriptions`,
        params: {
          company_id: localeToKlaviyoPublicMap(
            locale,
            process.env.GATSBY_KLAVIYO_BRAND
          ).publicKey,
        },
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2025-07-15',
          'content-type': 'application/vnd.api+json',
        },
        data: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: userEmail,
                  },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantIdNumber}`,
                },
              },
            },
          },
        }),
      });

      if (marketingOptIn) {
        await axios.post(process.env.GATSBY_KLAVIYO_OPT_IN_API, {
          email: userEmail,
          phone_number: customer?.phone || undefined,
        });
      }

      window.localStorage.setItem(oosKey, 'true');
      setOosToken(true);
    } catch (error) {
      console.error('Error during subscription:', error);

      const errorMessage =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : tt(s => s.outOfStockFormComponent.subscriptionErrorMessage) ||
            'An error occurred while subscribing. Please try again later.';

      setFormErrors(errorMessage);
    } finally {
      setIsLoading(false);
      setSubmitDisabled(false);
    }
  };

  return (
    <div className="flex pt-0 flex-col w-full">
      {oosToken === true ? (
        <p className="text-base leading-extraTight font-medium">
          {tt(s => s.outOfStockFormComponent.outOfStockMessageLabel)}
        </p>
      ) : (
        <form name="oosSignup" onSubmit={handleSubmit(handleEmailSignup)}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="userEmail">
              {tt(s => s.outOfStockFormComponent.formLabel)}
            </Label>
            <div className="flex w-full max-w-sm items-center">
              <Input
                id="userEmail"
                type="email"
                aria-describedby="out-of-stock-signup"
                defaultValue={customer?.email || ''}
                {...register('userEmail')}
                className="rounded-none rounded-l-lg"
              />
              <Button
                type="submit"
                aria-label={tt(
                  s => s.outOfStockFormComponent.submitButtonAreaLabel
                )}
                disabled={submitDisabled}
                variant="cta"
                className="rounded-s-none rounded-r-lg p-3"
              >
                {isLoading ? (
                  <svg
                    aria-hidden="true"
                    role="status"
                    className="inline w-5 h-5 text-gray-200 animate-spin shrink-0"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="white/80"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      className="fill-cta-foreground"
                    />
                  </svg>
                ) : (
                  <MdMailOutline size="24" />
                )}
              </Button>
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="marketingOptIn"
                checked={marketingOptIn}
                onCheckedChange={checked =>
                  setMarketingOptIn(checked as boolean)
                }
                className="opacity-100"
              />
              <label
                htmlFor="marketingOptIn"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {tt(
                  s =>
                    s.outOfStockFormComponent.marketingOptInLabel ||
                    'Email me with news and offers'
                )}
              </label>
            </div>
            {process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY && (
              <div className="invisible none fixed bottom-0 left-0 top-0">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.GATSBY_PETSAFE_RECAPTCHA_SITE_KEY}
                  hl={recaptchaLocale}
                />
              </div>
            )}
            {errors.userEmail && (
              <p
                id="out-of-stock-signup"
                aria-live="polite"
                className="mb-0 text-sm text-destructive font-medium"
              >
                {errors.userEmail.message}
              </p>
            )}
            {formErrors && (
              <p
                id="out-of-stock-signup"
                aria-live="polite"
                className="mb-0 text-sm text-destructive font-medium"
              >
                {formErrors}
              </p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
